import {
  LayoutDashboard, Package, Download, Truck, ArrowRightLeft,
  List, Factory, Wrench, Users, Building2, FileText, Tag, FlaskConical,
  Trash2, ScanLine, ShoppingCart, PackageCheck, RefreshCw, KeyRound,
  DollarSign, BarChart3, MapPin, Layers,
  ClipboardList, ScrollText, ClipboardCheck, Wheat,
  FolderArchive, FileCog, FolderTree, Tags, Grid3X3, Ruler,
  Store, PackageSearch, GitCompare
} from 'lucide-react';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import type { WarehouseSection } from '../../lib/types/manufacturing';
import { applyMenuRules, DEFAULT_MENU_CTX, type MenuCtx, type MenuVisibility } from '../../lib/commerce/menuRules';
import { COMMERCE_SECTIONS, type CommerceSection } from '../../lib/commerce/sections';

export type { MenuCtx } from '../../lib/commerce/menuRules';

// ============================================
// TYPES
// ============================================

export interface SidebarItem {
  id: WarehouseSection;
  label: string;
  icon: React.ElementType;
  badge?: string;
  /** Numero o pallino dinamico (08 sez. 4): lo legge la sidebar da useCommerceMenuSignals. */
  badgeKey?: 'commerce_conflicts' | 'commerce_attention';
  /** Visibile solo con negozi collegati ('commerce') o con WooCommerce sul sistema vecchio ('legacyWoo'). */
  visibleWhen?: MenuVisibility;
  /** Fase dell'interfaccia e-commerce da cui la voce compare (lib/commerce/phase.ts). */
  minPhase?: number;
}

export interface SidebarGroup {
  groupKey: string;
  label: string;
  separatorBefore?: string;
  items: SidebarItem[];
}

export interface WarehouseMenuConfig {
  groupLabels?: Record<string, string>;
  /** Sezione E-commerce accesa nel menu anche senza negozi collegati (Impostazioni > Voci menu). */
  commerceEnabled?: boolean;
  layout?: Array<{ groupKey: string; items: string[] }>;
}

// ============================================
// SIDEBAR GROUPS — label generiche di base
// ============================================

/**
 * Le sezioni e-commerce non sono ancora nel tipo WarehouseSection (src/lib/types/manufacturing.ts):
 * finche' non ci entrano, l'id passa da qui. Il valore e' lo stesso, cambia solo il tipo.
 */
const commerceSection = (id: CommerceSection): WarehouseSection => id as string as WarehouseSection;

export const SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    groupKey: 'panoramica',
    label: 'Panoramica',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    groupKey: 'area_acquisti',
    label: 'Area Acquisti',
    items: [
      { id: 'supplier_price_lists', label: 'Listini fornitori', icon: ScrollText },
      { id: 'supplier_orders', label: 'Ordini fornitori', icon: ClipboardList },
      { id: 'inbound_ddts', label: 'Entrata Merce', icon: FileText },
    ],
  },
  {
    groupKey: 'area_vendita',
    label: 'Area Vendita',
    items: [
      { id: 'price_lists', label: 'Listini', icon: DollarSign },
      { id: 'price_grid', label: 'Griglia Prezzi', icon: Grid3X3 },
      { id: 'physical_grid', label: 'Proprietà Fisiche', icon: Ruler },
      { id: 'orders', label: 'Ordini Clienti', icon: ShoppingCart },
      { id: 'outbound_ddts', label: 'Spedizione', icon: Truck },
      { id: 'order_preparation', label: 'Preparazione ordini', icon: PackageCheck },
      { id: 'rentals', label: 'Noleggi', icon: KeyRound },
    ],
  },
  {
    // Gruppo E-commerce (08 sez. 4): compare dal primo negozio collegato (hasCommerce, anche in analisi)
    // o per le org con WooCommerce sul sistema vecchio. Le due voci WooCommerce restano finche' l'org
    // non passa al motore (fase 3).
    groupKey: 'ecommerce',
    label: 'E-commerce',
    items: [
      { id: commerceSection(COMMERCE_SECTIONS.overview), label: 'Panoramica', icon: LayoutDashboard, visibleWhen: 'commerce', badgeKey: 'commerce_attention' },
      { id: commerceSection(COMMERCE_SECTIONS.stores), label: 'Negozi', icon: Store, visibleWhen: 'commerce' },
      { id: commerceSection(COMMERCE_SECTIONS.products), label: 'Prodotti nei negozi', icon: PackageSearch, visibleWhen: 'commerce', minPhase: 2 },
      { id: commerceSection(COMMERCE_SECTIONS.conflicts), label: 'Conflitti', icon: GitCompare, visibleWhen: 'commerce', minPhase: 2, badgeKey: 'commerce_conflicts' },
      { id: commerceSection(COMMERCE_SECTIONS.syncs), label: 'Sincronizzazioni', icon: RefreshCw, visibleWhen: 'commerce' },
      { id: 'woocommerce_sync', label: 'Sync WooCommerce', icon: RefreshCw, visibleWhen: 'legacyWoo' },
      { id: 'woo_clients', label: 'Clienti WooCommerce', icon: Users, visibleWhen: 'wooUsers' },
    ],
  },
  {
    groupKey: 'area_produzione',
    label: 'Area Produzione',
    items: [
      { id: 'production_cycles', label: 'Trasformazione', icon: Factory },
      { id: 'lots', label: 'Lotti', icon: Tag },
      { id: 'bom', label: 'Distinte base', icon: Layers },
      { id: 'all_movements', label: 'Movimentazioni', icon: List },
      { id: 'external_processings', label: 'Lavorazioni esterne', icon: Wrench },
      { id: 'rice_processing', label: 'Rese', icon: Wheat },
      { id: 'quality_controls', label: 'Controlli qualità', icon: ClipboardCheck },
      { id: 'lot_samples', label: 'Campioni di lotto', icon: FlaskConical },
      { id: 'traceability', label: 'Tracciabilità', icon: ScanLine },
    ],
  },
  {
    groupKey: 'area_gestione',
    label: 'Area Gestione',
    items: [
      { id: 'products', label: 'Prodotti', icon: Package },
      { id: 'categories', label: 'Categorie', icon: FolderTree },
      { id: 'attributes', label: 'Attributi', icon: Tags },
      { id: 'stock_loading', label: 'Inventario', icon: Download },
      { id: 'suppliers', label: 'Fornitori', icon: Users },
      { id: 'contractors', label: 'Terzisti', icon: Building2 },
      { id: 'zones', label: 'Magazzini', icon: MapPin },
      { id: 'zone_transfers', label: 'Trasferimenti zona', icon: ArrowRightLeft },
      { id: 'document_types', label: 'Documenti', icon: FileCog },
      { id: 'document_archive', label: 'Archivio documenti', icon: FolderArchive },
      { id: 'disposals', label: 'Registro smaltimenti', icon: Trash2 },
      { id: 'label_templates', label: 'Etichette & template', icon: Tag },
      { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    ],
  },
];

// ============================================
// LOOKUP MAPS
// ============================================

/** Mappa id → item completo (con icona) */
const ITEM_MAP: Record<string, SidebarItem> = Object.fromEntries(
  SIDEBAR_GROUPS.flatMap(g => g.items).map(i => [i.id, i])
);

/** Gruppo E-commerce nella forma del layout (per la regola di aggiunta automatica). */
const COMMERCE_LAYOUT_GROUP = (() => {
  const g = SIDEBAR_GROUPS.find(x => x.groupKey === 'ecommerce')!;
  return { groupKey: g.groupKey, items: g.items.map(i => i.id as string) };
})();

/**
 * Layout di default di prima del motore: niente gruppo E-commerce, le due voci WooCommerce in Area
 * Gestione dove stavano. Si usa solo quando il chiamante non passa il contesto (sidebar non ancora
 * aggiornata): cosi' il menu resta identico per tutti finche' il contesto non arriva (principio 4).
 */
const PRE_ENGINE_DEFAULT_LAYOUT: ReadonlyArray<{ groupKey: string; items: string[] }> = SIDEBAR_GROUPS
  .filter(g => g.groupKey !== 'ecommerce')
  .map(g => {
    const items = g.items.map(i => i.id as string);
    if (g.groupKey === 'area_gestione') {
      items.splice(items.indexOf('contractors') + 1, 0, 'woo_clients');
      items.push('woocommerce_sync');
    }
    return { groupKey: g.groupKey, items };
  });

/** Layout di partenza: quello salvato (solo voci esistenti) o quello di default, poi le regole e-commerce. */
function ruledLayout(
  config: WarehouseMenuConfig | undefined,
  ctx: MenuCtx
): Array<{ groupKey: string; items: string[] }> {
  const layout = config?.layout;
  const saved = !!layout && layout.length > 0;
  const defaults = ctx === DEFAULT_MENU_CTX
    ? PRE_ENGINE_DEFAULT_LAYOUT.map(g => ({ groupKey: g.groupKey, items: [...g.items] }))
    : SIDEBAR_GROUPS.map(g => ({ groupKey: g.groupKey, items: g.items.map(i => i.id as string) }));
  const base = saved
    ? layout!.map(lg => ({ groupKey: lg.groupKey, items: lg.items.filter(id => id in ITEM_MAP) }))
    : defaults;
  return applyMenuRules(base, saved, id => ITEM_MAP[id], COMMERCE_LAYOUT_GROUP, ctx);
}

/** Mappa groupKey → label di default */
const GROUP_LABEL_MAP: Record<string, string> = Object.fromEntries(
  SIDEBAR_GROUPS.map(g => [g.groupKey, g.label])
);

/** Mappa id → label generica di base */
const GENERIC_LABEL_MAP: Record<string, string> = Object.fromEntries(
  SIDEBAR_GROUPS.flatMap(g => g.items).map(i => [i.id, i.label])
);

/** Struttura piatta per settings (senza icone) */
export const WAREHOUSE_MENU_GROUPS = SIDEBAR_GROUPS.map(g => ({
  groupKey: g.groupKey,
  label: g.label,
  items: g.items.map(i => ({ id: i.id, label: i.label })),
}));

// ============================================
// LABEL RESOLUTION
// ============================================

export function resolveLabel(
  itemId: string,
  customLabels: Record<string, string>
): string {
  return customLabels[itemId] ?? GENERIC_LABEL_MAP[itemId] ?? itemId;
}

export function getDefaultLabel(itemId: string): string {
  return GENERIC_LABEL_MAP[itemId] ?? itemId;
}

export function resolveGroupLabel(
  groupKey: string,
  groupLabels: Record<string, string>
): string {
  return groupLabels[groupKey] ?? GROUP_LABEL_MAP[groupKey] ?? groupKey;
}

export function getDefaultGroupLabel(groupKey: string): string {
  return GROUP_LABEL_MAP[groupKey] ?? groupKey;
}

// ============================================
// BUILD RESOLVED GROUPS (used by Sidebar)
// ============================================

/**
 * Costruisce i gruppi risolti con layout custom, label custom, e voci nascoste.
 * Usato sia dalla Sidebar che dalle Settings.
 * `ctx` (facoltativo) dice se l'org ha negozi, WooCommerce vecchio e il permesso settings_ecommerce.view:
 * senza, il menu resta come prima del motore (voci e-commerce nascoste, voci WooCommerce vecchie visibili).
 */
export function buildResolvedGroups(
  config: WarehouseMenuConfig | undefined,
  hiddenSections: string[],
  customLabels: Record<string, string>,
  ctx: MenuCtx = DEFAULT_MENU_CTX
): SidebarGroup[] {
  const groupLabels = config?.groupLabels ?? {};
  const saved = !!config?.layout && config.layout.length > 0;

  const groups: SidebarGroup[] = ruledLayout(config, ctx).map(lg => {
    const def = SIDEBAR_GROUPS.find(g => g.groupKey === lg.groupKey);
    return {
      groupKey: lg.groupKey,
      label: resolveGroupLabel(lg.groupKey, groupLabels),
      ...(!saved && def?.separatorBefore ? { separatorBefore: def.separatorBefore } : {}),
      items: lg.items
        .map(id => ITEM_MAP[id])
        .filter((item): item is SidebarItem => !!item),
    };
  });

  // Filtra voci nascoste e risolve label
  return groups
    .map(group => ({
      ...group,
      items: group.items
        .filter(item => !hiddenSections.includes(item.id))
        .map(item => ({
          ...item,
          label: resolveLabel(item.id, customLabels),
        })),
    }))
    .filter(group => group.items.length > 0);
}

/**
 * Ritorna il layout corrente (custom o default) come array editabile per le Settings.
 * Include TUTTE le voci (anche nascoste), perché le settings devono mostrarle tutte.
 */
export function getEditableLayout(
  config: WarehouseMenuConfig | undefined,
  ctx: MenuCtx = DEFAULT_MENU_CTX
): Array<{ groupKey: string; items: string[] }> {
  // Stesse regole della sidebar: niente voci "fantasma" e-commerce per chi non ha negozi (15 sez. 3.1)
  return ruledLayout(config, ctx);
}

/** Lookup per avere icona e badge di un item dato il suo ID */
export function getItemMeta(itemId: string): { icon: React.ElementType; badge?: string; badgeKey?: SidebarItem['badgeKey'] } | undefined {
  const item = ITEM_MAP[itemId];
  return item ? { icon: item.icon, badge: item.badge, badgeKey: item.badgeKey } : undefined;
}

// ============================================
// HOOK — risolve la label di una sezione
// ============================================

/**
 * Hook per ottenere la label risolta di una sezione warehouse.
 * Da usare nei componenti delle sezioni al posto dei titoli hardcoded.
 */
export function useWarehouseSectionLabel(sectionId: string): string {
  const { settings } = useSettingsStore();
  const customLabels = (settings?.warehouse_menu_labels as Record<string, string>) || {};
  return resolveLabel(sectionId, customLabels);
}
